import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Globe2, LayoutDashboard, FileText, FileSpreadsheet, Users, Package, LogOut, Settings, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { NotificationBell } from "@/components/app/NotificationBell";

const BUYER_NAV = [
  { to: "/buyer", label: "Overview", Icon: LayoutDashboard, end: true },
  { to: "/buyer/new", label: "New sourcing request", Icon: FileText },
  { to: "/buyer/requests", label: "My requests", Icon: FileSpreadsheet },
  { to: "/buyer/orders", label: "Orders", Icon: Package },
];
const SUPPLIER_NAV = [
  { to: "/supplier", label: "Overview", Icon: LayoutDashboard, end: true },
  { to: "/supplier/rfqs", label: "Incoming RFQs", Icon: FileText },
  { to: "/supplier/profile", label: "Company profile", Icon: Settings },
];
const ADMIN_NAV = [
  { to: "/admin", label: "Platform overview", Icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users", Icon: Users },
  { to: "/admin/suppliers", label: "Suppliers", Icon: Package },
];

export function DashLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  if (!user) return null;
  const items = user.role === "buyer" ? BUYER_NAV : user.role === "supplier" ? SUPPLIER_NAV : ADMIN_NAV;
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Top bar (mobile only) */}
      <div className="md:hidden sticky top-0 z-40 h-14 border-b border-white/10 bg-[hsl(var(--navy))] text-slate-100 flex items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          <span className="h-7 w-7 rounded-sm bg-[hsl(var(--gold))] flex items-center justify-center text-[hsl(var(--navy))]"><Globe2 size={16}/></span>
          <span className="font-semibold tracking-tight">GlobalBuy <span className="text-[hsl(var(--gold))]">AI</span></span>
        </Link>
        <div className="flex items-center gap-2">
          <NotificationBell dark />
          <button onClick={() => setOpen((o) => !o)} aria-label="Toggle navigation" data-testid="mobile-nav-toggle" className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-white/10">
            {open ? <X size={18}/> : <Menu size={18}/>}
          </button>
        </div>
      </div>

      <div className="md:flex">
        {/* Sidebar — desktop sticky, mobile overlay */}
        <aside className={`${open ? "block" : "hidden"} md:block fixed md:sticky top-14 md:top-0 inset-x-0 md:inset-x-auto h-[calc(100vh-3.5rem)] md:h-screen md:w-64 w-full bg-[hsl(var(--navy))] text-slate-100 border-r border-white/10 z-30 overflow-y-auto`}>
          <div className="hidden md:flex items-center gap-2 px-5 h-16 border-b border-white/10">
            <Link to="/" className="flex items-center gap-2" data-testid="sidebar-brand-link">
              <span className="h-8 w-8 rounded-sm bg-[hsl(var(--gold))] flex items-center justify-center text-[hsl(var(--navy))]"><Globe2 size={18}/></span>
              <span className="font-semibold tracking-tight text-lg">GlobalBuy <span className="text-[hsl(var(--gold))]">AI</span></span>
            </Link>
          </div>
          <nav className="px-3 py-4 space-y-1">
            {items.map(({ to, label, Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-white/5 text-white border-l-2 border-[hsl(var(--gold))] pl-[10px]" : "text-slate-300 hover:text-white hover:bg-white/5"
                  }`
                }
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon size={16}/> {label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-6 p-4 border-t border-white/10">
            <div className="text-xs text-slate-400 mb-2">
              <div className="text-slate-200 font-medium truncate" data-selectable>{user.full_name}</div>
              <div className="truncate" data-selectable>{user.email}</div>
              <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[hsl(var(--gold))]">{user.role}</div>
            </div>
            <button onClick={() => { logout(); nav("/"); }} data-testid="dash-logout-btn" className="w-full inline-flex items-center justify-center gap-2 h-9 rounded-md border border-white/10 hover:bg-white/5 text-sm transition-colors">
              <LogOut size={14}/> Sign out
            </button>
          </div>
        </aside>

        <main className="flex-1 min-w-0 min-h-screen">
          {/* Desktop top bar — right-aligned notification bell */}
          <div className="hidden md:flex items-center justify-end gap-3 h-16 px-6 md:px-8 border-b border-slate-200 bg-white/60 backdrop-blur sticky top-0 z-30">
            <NotificationBell />
          </div>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-10 pb-24">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
